import { createSelector } from 'reselect';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';

function filterBySlug(data, slug) {
  if (_isEmpty(data)) return {};
  return data.filter(el => el.slug === slug)[0];
}

function filterByTitle(data, title) {
  if (_isEmpty(data)) return {};
  return data.filter(el => el.title === title)[0];
}

const selectMainDomain = state => state.main;

const makeSelectProcessing = () =>
  createSelector(
    selectMainDomain,
    substate => substate.processing,
  );

const makeSelectClientDetails = () =>
  createSelector(
    selectMainDomain,
    substate => substate.clientDetails,
  );

const makeSelectClientDetailsLoaded = () =>
  createSelector(
    selectMainDomain,
    substate => substate.clientDetailsLoaded,
  );

const makeSelectAllowSignIn = () =>
  createSelector(
    selectMainDomain,
    substate => {
      const signInStatus = _get(substate.clientDetails, 'signIn');

      if (signInStatus) {
        return signInStatus !== 'Prevent';
      }
      return true;
    },
  );

const makeSelectOAuthOptions = () =>
  createSelector(
    selectMainDomain,
    substate => {
      const msAllowed = _get(substate.clientDetails, ['msOAuthSignIn']);
      const googleAllowed = _get(substate.clientDetails, ['googleSignIn']);
      const facebookAllowed = _get(substate.clientDetails, ['facebookSignIn']);
      const cmAllowed = _get(substate.clientDetails, ['cmSignIn']);

      const options = {
        ms: msAllowed !== undefined ? msAllowed : true,
        google: googleAllowed !== undefined ? googleAllowed : true,
        facebook: facebookAllowed !== undefined ? facebookAllowed : true,
        cm: cmAllowed !== undefined ? cmAllowed : true,
      };

      return options;
    },
  );

const makeSelectShowingTermsOfUse = () =>
  createSelector(
    selectMainDomain,
    substate => substate.showingTermsOfUse,
  );

const makeSelectShowingPrivacyPolicy = () =>
  createSelector(
    selectMainDomain,
    substate => substate.showingPrivacyPolicy,
  );
const makeSelectShowingFeedback = () =>
  createSelector(
    selectMainDomain,
    substate => substate.showingFeedback,
  );

const makeSelectFooter = () =>
  createSelector(
    selectMainDomain,
    substate => filterBySlug(substate.siteCopy, 'footer'),
  );
const makeSelectHomeHero = () =>
  createSelector(
    selectMainDomain,
    substate => filterBySlug(substate.siteCopy, 'home-hero'),
  );
const makeSelectSendMailStatus = () =>
  createSelector(
    selectMainDomain,
    substate => substate.mailSent,
  );
const makeSelectSendMailProcessing = () =>
  createSelector(
    selectMainDomain,
    substate => substate.sendMailProcessing,
  );
const makeSelectHomeHeaders = () =>
  createSelector(
    selectMainDomain,
    substate => filterBySlug(substate.siteCopy, 'home-section-headers'),
  );
const makeSelectFavoritesHeaders = () =>
  createSelector(
    selectMainDomain,
    substate => filterBySlug(substate.siteCopy, 'favorites-section-headers'),
  );
const makeSelectPrivacyPolicy = () =>
  createSelector(
    selectMainDomain,
    substate => filterBySlug(substate.siteCopy, 'privacy'),
  );
const makeSelectTermsOfUse = () =>
  createSelector(
    selectMainDomain,
    substate => filterBySlug(substate.siteCopy, 'terms-of-use'),
  );
const makeSelectFeedback = () =>
  createSelector(
    selectMainDomain,
    substate => filterBySlug(substate.siteCopy, 'feedback'),
  );
const makeSelectSiteCopy = () =>
  createSelector(
    selectMainDomain,
    substate => substate.siteCopy,
  );
const makeSelectSiteCopyLanguage = () =>
  createSelector(
    selectMainDomain,
    substate => substate.siteCopyLanguage,
  );
const makeSelectSiteCopyInOtherLang = () =>
  createSelector(
    selectMainDomain,
    substate => substate.siteCopyInOtherLang,
  );
const makeSelectSearchConfigInOtherLang = () =>
  createSelector(
    selectMainDomain,
    substate => substate.searchCopyInOtherLang,
  );
const makeSelectSiteConfig = () =>
  createSelector(
    selectMainDomain,
    substate => substate.siteConfig,
  );

const makeSelectProgramsByTopic = () =>
  createSelector(
    selectMainDomain,
    substate => substate.programs,
  );
const makeSelectThemes = () =>
  createSelector(
    selectMainDomain,
    substate => substate.themes,
  );
const makeSelectResourcesCountByTopic = () =>
  createSelector(
    selectMainDomain,
    substate => substate.resourcesCountByTopic,
  );
const makeSelectClientDetailsFetching = () =>
  createSelector(
    selectMainDomain,
    substate => substate.clientDetailsFetching,
  );
const makeSelectClientResources = () =>
  createSelector(
    selectMainDomain,
    substate => substate.clientResources,
  );
const makeSelectShowRegionSelector = () =>
  createSelector(
    selectMainDomain,
    substate => substate.showRegionSelector,
  );
const makeSelectRegion = () =>
  createSelector(
    selectMainDomain,
    substate => substate.region,
  );
const makeSelectShowRegionBanner = () =>
  createSelector(
    selectMainDomain,
    substate => substate.showRegionBanner,
  );
const makeSelectLandingMenu = () =>
  createSelector(
    selectMainDomain,
    substate => substate.landingMenu,
  );
const makeSelectShowHomepagePro = () =>
  createSelector(
    selectMainDomain,
    substate => substate.showHomepagePro,
  );
const makeSelectAudienceTagsRelations = () =>
  createSelector(
    selectMainDomain,
    substate => substate.audienceTagsRelations,
  );
const makeSelectNPSVisibility = () =>
  createSelector(
    selectMainDomain,
    substate => substate.npsVisibility,
  );
const makeSelectNPSPlayCount = () =>
  createSelector(
    selectMainDomain,
    substate => substate.npsPlayCount,
  );
const makeSelectNPSPreconditions = () =>
  createSelector(
    selectMainDomain,
    substate => substate.npsPreconditions,
  );

const makeSelectHelpfulLocation = () =>
  createSelector(
    selectMainDomain,
    substate => substate.helpfulLocation,
  );

const makeSelectSaveRemindMeLaterResourceResult = () =>
  createSelector(
    selectMainDomain,
    substate => substate.saveRemindMeResourceResult,
  );

const makeSelectGetRemindMeLaterResourceResult = () =>
  createSelector(
    selectMainDomain,
    substate => substate.getRemindMeResourceResult,
  );

const makeSelectPageIntros = () =>
  createSelector(
    selectMainDomain,
    substate => substate.pageIntros,
  );

const makeSelectLanguage = () =>
  createSelector(
    selectMainDomain,
    substate => substate.language,
  );
const makeSelectShowLanguageBanner = () =>
  createSelector(
    selectMainDomain,
    substate => substate.showLanguageBanner,
  );

const makeSelectLocales = () =>
  createSelector(
    selectMainDomain,
    substate => substate.locales,
  );
const makeSelectLeavingModal = () =>
  createSelector(
    selectMainDomain,
    substate => substate.leavingModal,
  );
const makeSelectAICCUser = () =>
  createSelector(
    selectMainDomain,
    substate => substate.aiccUser,
  );
const makeSelectAICCRedirected = () =>
  createSelector(
    selectMainDomain,
    substate => substate.aiccRedirected,
  );
const makeSelectReferrals = () =>
  createSelector(
    selectMainDomain,
    substate => substate.referrals,
  );
const makeSelectClinicMode = () =>
  createSelector(
    selectMainDomain,
    substate => substate.clinicMode,
  );

const makeSelectShowOnboardingQuestions = () =>
  createSelector(
    selectMainDomain,
    substate => substate.showOnboardingQuestions,
  );

const makeSelectInsuranceModal = () =>
  createSelector(
    selectMainDomain,
    substate => substate.insuranceModal,
  );

const makeSelectInsuranceOptions = () =>
  createSelector(
    selectMainDomain,
    substate => {
      const shortName = _get(substate, 'clientDetails.shortName');
      const locale = _get(substate, 'language');
      const parentTitle = _get(
        substate,
        'clientDetails.clientParentCollection.items.0.title',
      );

      const insuranceSiteConfig = filterByTitle(
        substate.siteConfig,
        'Insurance',
      );
      const insuranceOptions = _get(
        insuranceSiteConfig,
        'config.options',
        [],
      ).filter(item => {
        if (
          item.exclude.includes(shortName) ||
          (parentTitle && item.exclude.includes(parentTitle))
        )
          return false;
        if (!_isEmpty(item.client)) {
          if (
            item.client.includes(shortName) ||
            (parentTitle && item.client.includes(parentTitle))
          )
            return true;
          return false;
        }
        return true;
      });

      return insuranceOptions.map(option => ({
        ...option,
        label: _get(option, ['label', locale], option.value),
      }));
    },
  );

export {
  makeSelectProcessing,
  makeSelectClientDetailsFetching,
  makeSelectClientDetails,
  makeSelectClientDetailsLoaded,
  makeSelectAllowSignIn,
  makeSelectOAuthOptions,
  makeSelectShowingTermsOfUse,
  makeSelectShowingPrivacyPolicy,
  makeSelectShowingFeedback,
  makeSelectFooter,
  makeSelectHomeHero,
  makeSelectSendMailStatus,
  makeSelectSendMailProcessing,
  makeSelectHomeHeaders,
  makeSelectFavoritesHeaders,
  makeSelectSiteCopy,
  makeSelectSiteCopyLanguage,
  makeSelectSiteCopyInOtherLang,
  makeSelectSearchConfigInOtherLang,
  makeSelectSiteConfig,
  makeSelectPrivacyPolicy,
  makeSelectTermsOfUse,
  makeSelectFeedback,
  makeSelectProgramsByTopic,
  makeSelectThemes,
  makeSelectResourcesCountByTopic,
  makeSelectClientResources,
  makeSelectShowRegionSelector,
  makeSelectRegion,
  makeSelectShowRegionBanner,
  makeSelectLandingMenu,
  makeSelectShowHomepagePro,
  makeSelectAudienceTagsRelations,
  makeSelectNPSVisibility,
  makeSelectNPSPlayCount,
  makeSelectNPSPreconditions,
  makeSelectHelpfulLocation,
  makeSelectSaveRemindMeLaterResourceResult,
  makeSelectGetRemindMeLaterResourceResult,
  makeSelectPageIntros,
  makeSelectReferrals,
  makeSelectLanguage,
  makeSelectShowLanguageBanner,
  makeSelectLocales,
  makeSelectLeavingModal,
  makeSelectAICCUser,
  makeSelectAICCRedirected,
  makeSelectClinicMode,
  makeSelectShowOnboardingQuestions,
  makeSelectInsuranceModal,
  makeSelectInsuranceOptions,
};
